import { TILE_SIZE } from '@/sprites/tiles';
import { Area, type AreaDefinition } from '@/world/area';
import { generateFloor } from '@/dungeon/generator';

/**
 * A dungeon floor, dressed as an area.
 *
 * Every other place in the game is authored data in `src/data/maps` and cached
 * forever by `getArea`. A floor is neither: it is generated, there is a new one
 * every time you go down, and caching them would grow without limit while
 * pinning layouts that are supposed to be regenerated.
 *
 * So floors are built here instead of joining the registry. They still produce
 * an ordinary `Area`, which is what lets the renderer, collision and the spawn
 * director carry on knowing nothing about dungeons.
 */

/** How deep you can go. Beyond this the stairs down simply are not drawn. */
export const MAX_DEPTH = 20;

export function dungeonFloorName(depth: number): string {
  return `The Deep — Floor ${depth}`;
}

export function makeDungeonArea(seed: number, depth: number): Area {
  const floor = generateFloor({ seed, depth });

  const definition: AreaDefinition = {
    id: 'dungeon',
    name: dungeonFloorName(depth),
    rows: floor.rows,
    // Arriving stands you on the way back up, which is also how you leave.
    spawn: { x: floor.entrance.x * TILE_SIZE, y: floor.entrance.y * TILE_SIZE },
    // Stairs are tiles, not portals: a portal carries one fixed destination,
    // and "one floor further down" is not one.
    portals: [],
    // Underground. No weather, and lit by whatever you brought.
    indoor: true,
  };

  return new Area(definition);
}
